import React, { useState, useEffect, useRef } from 'react';
import { Header } from '../../shared/layout/Header';
import {
  getDailyLeaderboard,
  getWeeklyLeaderboard,
  getBestPlayersLeaderboard,
  getBestScoresLeaderboard
} from './utils/scoreUtils';
import {
  getDailyGuesserLeaderboard,
  getWeeklyGuesserLeaderboard,
  getBestGuesserScores,
  getBestGuesserPlayers
} from '../campus-guesser/services/leaderboardService';
import { getEasternDate } from './utils/dailyWordUtils';
import { trackLeaderboardViewed, trackLeaderboardFilterChanged } from '../../core/utils/analytics';
import GuessModal from '../../shared/modals/GuessModal';
import { GameSelector, getGameConfig } from '../../shared/leaderboard';
import { MapPin, ChevronDown, Check } from 'lucide-react';
import './Leaderboard.css';

export default function Leaderboard() {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGame, setSelectedGame] = useState('wordle'); // 'wordle' or 'campusGuesser'
  const [timeFilter, setTimeFilter] = useState('today'); // 'today', 'week', or 'alltime'
  const [alltimeView, setAlltimeView] = useState('players'); // 'players' or 'scores'
  const [alltimeDropdownOpen, setAlltimeDropdownOpen] = useState(false);
  const alltimeDropdownRef = useRef(null);

  // Guess Modal state
  const [showGuessModal, setShowGuessModal] = useState(false);
  const [selectedScoreId, setSelectedScoreId] = useState(null);
  const [selectedTargetWord, setSelectedTargetWord] = useState(null);

  // Get current game config
  const gameConfig = getGameConfig(selectedGame);

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedGame, timeFilter, alltimeView]);

  // Close alltime dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (alltimeDropdownRef.current && !alltimeDropdownRef.current.contains(event.target)) {
        setAlltimeDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);

    try {
      let data;
      const filterType = timeFilter === 'alltime' ? `${timeFilter}_${alltimeView}` : timeFilter;

      if (selectedGame === 'wordle') {
        // Wordle leaderboard logic
        if (timeFilter === 'today') {
          const todayString = getEasternDate();
          data = await getDailyLeaderboard(todayString, 50);
        } else if (timeFilter === 'week') {
          data = await getWeeklyLeaderboard(50);
        } else if (timeFilter === 'alltime') {
          if (alltimeView === 'players') {
            data = await getBestPlayersLeaderboard(50, 1);
          } else {
            data = await getBestScoresLeaderboard(50);
          }
        }
      } else if (selectedGame === 'campusGuesser') {
        // Campus Guesser leaderboard
        if (timeFilter === 'today') {
          const todayString = getEasternDate();
          data = await getDailyGuesserLeaderboard(todayString, 50);
        } else if (timeFilter === 'week') {
          data = await getWeeklyGuesserLeaderboard(50);
        } else if (timeFilter === 'alltime') {
          if (alltimeView === 'players') {
            data = await getBestGuesserPlayers(50, 1);
          } else {
            data = await getBestGuesserScores(50);
          }
        }
      }
      
      setLeaderboardData(data || []);

      // Track leaderboard view
      trackLeaderboardViewed(`${selectedGame}_${filterType}`);
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle game change
  const handleGameChange = (gameId) => {
    setSelectedGame(gameId);
    setTimeFilter('today'); // Reset to today when switching games
    setAlltimeView('players'); // Reset alltime view
  };

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // dateString is in format YYYY-MM-DD
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDistance = (meters) => {
    if (meters === undefined || meters === null) return '';
    if (meters < 1) {
      return '< 1m';
    } else if (meters < 1000) {
      return `${Math.round(meters)}m`;
    } else {
      return `${(meters / 1000).toFixed(2)}km`;
    }
  };

  const shouldHideWord = (entry) => {
    // Hide today's word to prevent spoilers
    const todayString = getEasternDate();

    // If we're on "Today's Word" tab, hide all words (they're all today's)
    if (timeFilter === 'today') {
      return true;
    }

    // For other tabs, check if the entry is from today
    return entry.dailyWordDate === todayString;
  };

  const handleGuessClick = (entry) => {
    // Don't fetch if this is the Best Players view (no individual games)
    if (timeFilter === 'alltime' && alltimeView === 'players') {
      return;
    }

    // Don't show target word if it's today's word (prevent spoilers)
    const hideWord = shouldHideWord(entry);
    setSelectedScoreId(entry.id);
    setSelectedTargetWord(hideWord ? null : entry.targetWord);
    setShowGuessModal(true);
  };

  const closeModal = () => {
    setShowGuessModal(false);
    setSelectedScoreId(null);
    setSelectedTargetWord(null);
  };

  return (
    <div className="leaderboard-page">
      <Header />

      <div className="leaderboard-container">
        <div className="leaderboard-header">
          <h1>Leaderboard</h1>
          <p className="leaderboard-subtitle">
            Howard University {gameConfig?.name || 'Gaming'} Champions
          </p>
        </div>

        {/* Game Selector Dropdown */}
        <GameSelector 
          selectedGame={selectedGame} 
          onGameChange={handleGameChange} 
        />

        <div className="time-filter">
          <button
            className={`filter-btn ${timeFilter === 'today' ? 'active' : ''}`}
            onClick={() => {
              const previousFilter = timeFilter === 'alltime' ? `${timeFilter}_${alltimeView}` : timeFilter;
              trackLeaderboardFilterChanged(previousFilter, 'today');
              setTimeFilter('today');
              setAlltimeDropdownOpen(false);
            }}
          >
            {selectedGame === 'wordle' ? "Today's Word" : "Today"}
          </button>
          <button
            className={`filter-btn ${timeFilter === 'week' ? 'active' : ''}`}
            onClick={() => {
              const previousFilter = timeFilter === 'alltime' ? `${timeFilter}_${alltimeView}` : timeFilter;
              trackLeaderboardFilterChanged(previousFilter, 'week');
              setTimeFilter('week');
              setAlltimeDropdownOpen(false);
            }}
          >
            This Week
          </button>
          
          {/* All Time Dropdown */}
          <div className="alltime-dropdown-container" ref={alltimeDropdownRef}>
            <button
              className={`filter-btn alltime-btn ${timeFilter === 'alltime' ? 'active' : ''}`}
              onClick={() => setAlltimeDropdownOpen(!alltimeDropdownOpen)}
            >
              {timeFilter === 'alltime' 
                ? (alltimeView === 'players' ? 'Best Players' : 'Best Scores')
                : 'All Time'
              }
              <ChevronDown 
                size={16} 
                className={`alltime-chevron ${alltimeDropdownOpen ? 'rotated' : ''}`} 
              />
            </button>
            
            {alltimeDropdownOpen && (
              <div className="alltime-dropdown">
                <button
                  className={`alltime-option ${timeFilter === 'alltime' && alltimeView === 'players' ? 'active' : ''}`}
                  onClick={() => {
                    trackLeaderboardFilterChanged(
                      timeFilter === 'alltime' ? `alltime_${alltimeView}` : timeFilter,
                      'alltime_players'
                    );
                    setTimeFilter('alltime');
                    setAlltimeView('players');
                    setAlltimeDropdownOpen(false);
                  }}
                >
                  <span>Best Players</span>
                  {timeFilter === 'alltime' && alltimeView === 'players' && <Check size={16} />}
                </button>
                <button
                  className={`alltime-option ${timeFilter === 'alltime' && alltimeView === 'scores' ? 'active' : ''}`}
                  onClick={() => {
                    trackLeaderboardFilterChanged(
                      timeFilter === 'alltime' ? `alltime_${alltimeView}` : timeFilter,
                      'alltime_scores'
                    );
                    setTimeFilter('alltime');
                    setAlltimeView('scores');
                    setAlltimeDropdownOpen(false);
                  }}
                >
                  <span>Best Scores</span>
                  {timeFilter === 'alltime' && alltimeView === 'scores' && <Check size={16} />}
                </button>
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="leaderboard-loading">
            <div className="spinner"></div>
            <p>Loading leaderboard...</p>
          </div>
        )}

        {error && (
          <div className="leaderboard-error">
            <p>{error}</p>
            <button onClick={fetchLeaderboard} className="retry-btn">
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && leaderboardData.length === 0 && (
          <div className="leaderboard-empty">
            {selectedGame === 'campusGuesser' ? (
              <>
                <MapPin size={48} className="empty-icon" />
                <p>No scores yet for Campus Guesser!</p>
                <p className="empty-subtext">Be the first to play and set a high score.</p>
              </>
            ) : (
              <p>No scores yet. Be the first to play!</p>
            )}
          </div>
        )}

        {!loading && !error && leaderboardData.length > 0 && (
          <div className={`leaderboard-table-container ${selectedGame === 'campusGuesser' ? 'campus-guesser-table' : 'wordle-table'}`}>
            {/* WORDLE TABLES */}
            {selectedGame === 'wordle' && timeFilter === 'alltime' && alltimeView === 'players' && (
              // Wordle Best Players view
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th className="rank-col">Rank</th>
                    <th className="username-col">Player</th>
                    <th className="avg-col">Avg Guesses</th>
                    <th className="wins-col">Games Won</th>
                    <th className="winrate-col">Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.map((entry, index) => (
                    <tr
                      key={entry.username}
                      className={`leaderboard-row ${index < 3 ? `top-${index + 1}` : ''}`}
                    >
                      <td className="rank-col">
                        {entry.isTied && index > 0 ? (
                          <span className="rank-tie">T{entry.rank}</span>
                        ) : (
                          <span className="rank-number">
                            {entry.rank === 1 && '🥇 '}
                            {entry.rank === 2 && '🥈 '}
                            {entry.rank === 3 && '🥉 '}
                            {entry.rank}
                          </span>
                        )}
                      </td>
                      <td className="username-col">
                        <span className="username">{entry.username}</span>
                      </td>
                      <td className="avg-col">
                        <span className="avg-badge">{entry.avgGuesses}</span>
                      </td>
                      <td className="wins-col">
                        {entry.gamesWon}
                      </td>
                      <td className="winrate-col">
                        {entry.winRate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {selectedGame === 'wordle' && !(timeFilter === 'alltime' && alltimeView === 'players') && (
              // Wordle Today, This Week, and Best Scores views
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th className="rank-col">Rank</th>
                    <th className="username-col">Player</th>
                    <th className="attempts-col">Guesses</th>
                    <th className="time-col">Time</th>
                    <th className="word-col">Word</th>
                    {(timeFilter === 'alltime' || timeFilter === 'week') && <th className="date-col">Date</th>}
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.map((entry, index) => (
                    <tr
                      key={entry.id}
                      className={`leaderboard-row ${index < 3 ? `top-${index + 1}` : ''}`}
                    >
                      <td className="rank-col">
                        {entry.isTied && index > 0 ? (
                          <span className="rank-tie">T{entry.rank}</span>
                        ) : (
                          <span className="rank-number">
                            {entry.rank === 1 && '🥇 '}
                            {entry.rank === 2 && '🥈 '}
                            {entry.rank === 3 && '🥉 '}
                            {entry.rank}
                          </span>
                        )}
                      </td>
                      <td className="username-col">
                        <span className="username">{entry.username}</span>
                      </td>
                      <td
                        className="attempts-col clickable"
                        onClick={() => handleGuessClick(entry)}
                        title="Click to view guesses"
                      >
                        <span className="attempts-badge">
                          {entry.attempts} {entry.attempts === 1 ? 'guess' : 'guesses'}
                        </span>
                      </td>
                      <td className="time-col">
                        {formatTime(entry.completionTimeMs)}
                      </td>
                      <td className="word-col">
                        {shouldHideWord(entry) ? '???' : entry.targetWord}
                      </td>
                      {(timeFilter === 'alltime' || timeFilter === 'week') && (
                        <td className="date-col">
                          {formatDate(entry.dailyWordDate)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* CAMPUS GUESSER TABLES */}
            {selectedGame === 'campusGuesser' && timeFilter === 'alltime' && alltimeView === 'players' && (
              // Campus Guesser Best Players view
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th className="rank-col">Rank</th>
                    <th className="username-col">Player</th>
                    <th className="avg-col">Avg Score</th>
                    <th className="distance-col">Avg Distance</th>
                    <th className="games-col">Games</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.map((entry, index) => (
                    <tr
                      key={entry.username}
                      className={`leaderboard-row ${index < 3 ? `top-${index + 1}` : ''}`}
                    >
                      <td className="rank-col">
                        {entry.isTied && index > 0 ? (
                          <span className="rank-tie">T{entry.rank}</span>
                        ) : (
                          <span className="rank-number">
                            {entry.rank === 1 && '🥇 '}
                            {entry.rank === 2 && '🥈 '}
                            {entry.rank === 3 && '🥉 '}
                            {entry.rank}
                          </span>
                        )}
                      </td>
                      <td className="username-col">
                        <span className="username">{entry.username}</span>
                      </td>
                      <td className="avg-col">
                        <span className="score-badge">{entry.avgScore}</span>
                      </td>
                      <td className="distance-col">
                        {formatDistance(entry.avgDistance)}
                      </td>
                      <td className="games-col">
                        {entry.gamesPlayed}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {selectedGame === 'campusGuesser' && !(timeFilter === 'alltime' && alltimeView === 'players') && (
              // Campus Guesser Today, This Week, and Best Scores views
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th className="rank-col">Rank</th>
                    <th className="username-col">Player</th>
                    <th className="score-col">Score</th>
                    <th className="distance-col">Distance</th>
                    <th className="time-col">Time</th>
                    {(timeFilter === 'alltime' || timeFilter === 'week') && <th className="date-col">Date</th>}
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.map((entry, index) => (
                    <tr
                      key={entry.id}
                      className={`leaderboard-row ${index < 3 ? `top-${index + 1}` : ''}`}
                    >
                      <td className="rank-col">
                        {entry.isTied && index > 0 ? (
                          <span className="rank-tie">T{entry.rank}</span>
                        ) : (
                          <span className="rank-number">
                            {entry.rank === 1 && '🥇 '}
                            {entry.rank === 2 && '🥈 '}
                            {entry.rank === 3 && '🥉 '}
                            {entry.rank}
                          </span>
                        )}
                      </td>
                      <td className="username-col">
                        <span className="username">{entry.username}</span>
                      </td>
                      <td className="score-col">
                        <span className="score-badge">{entry.score}</span>
                      </td>
                      <td className="distance-col">
                        {formatDistance(entry.distance)}
                      </td>
                      <td className="time-col">
                        {formatTime(entry.completionTimeMs)}
                      </td>
                      {(timeFilter === 'alltime' || timeFilter === 'week') && (
                        <td className="date-col">
                          {formatDate(entry.date)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="leaderboard-footer">
          {gameConfig?.footerMessages && (() => {
            const viewKey = timeFilter === 'alltime' ? `alltime_${alltimeView}` : timeFilter;
            const messages = gameConfig.footerMessages[viewKey] || [];
            return messages.map((msg, idx) => (
              <p key={idx} className="leaderboard-note">{msg}</p>
            ));
          })()}
        </div>
      </div>

      {/* Guess Details Modal */}
      <GuessModal
        isOpen={showGuessModal}
        onClose={closeModal}
        scoreId={selectedScoreId}
        targetWord={selectedTargetWord}
      />
    </div>
  );
}
